import { Injectable, inject, PLATFORM_ID, DOCUMENT } from '@angular/core';
import { TLangCode } from '../../../ngrx/locale/states/locale.state';
import { BROWSER_LANGUAGE_TOKEN } from '../browser-language/browser-language.token';
import { CookiesService } from '../../cookies/cookies.service';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../../environments/environment';



export type TLanguageType = 'public' | 'multilingual' | 'platform';

@Injectable({
  providedIn: 'root'
})
export class LocaleService {
    private platformId = inject(PLATFORM_ID);
    private cookiesService = inject(CookiesService);
    private browserLang = inject(BROWSER_LANGUAGE_TOKEN);
    private document = inject(DOCUMENT);

    MULTILINGUAL_PREFERENCE = 'language_preference';
    PLATFORM_LANGUAGE_PREFERENCE = 'platform_language_preference';

    private inMemoryLocale: TLangCode;

    constructor() { }

    /**
     * Get current locale from memory or initialize from user sidebar
     */
    public get locale(): TLangCode {
        this.inMemoryLocale = this.inMemoryLocale ?? this.getUserLanguage({
          supportedLang: environment.languages.map(lang => lang.code) as TLangCode[],
          defaultLang: 'no',
          languageType: 'platform'
        });

        return this.inMemoryLocale;
    }

    /**
     * Set current locale and persist to cookies
     */
    public set locale(locale: TLangCode) {
      if (isPlatformBrowser(this.platformId)) {
        this.cookiesService.setCookie({
          name: this.PLATFORM_LANGUAGE_PREFERENCE,
          value: locale,
          expireDays: 360
        });
      }

      this.inMemoryLocale = locale;
    }


    setMultilingualPreference(code: TLangCode): void {
      this.cookiesService.setCookie({
        name: this.MULTILINGUAL_PREFERENCE,
        value: code,
        expireDays: 360
      });

      this.document.documentElement.lang = code;
    }

    getLanguagePreference(type: TLanguageType = 'multilingual'): TLangCode | null {
      let cookieName: string;

      switch (type) {
        case 'multilingual':
          cookieName = this.MULTILINGUAL_PREFERENCE;
          break;

        case 'platform':
          cookieName = this.PLATFORM_LANGUAGE_PREFERENCE;
          break;
      }

      const cookie = this.cookiesService.getCookie(cookieName);
      return cookie ? cookie as TLangCode : null;
    }

  /**
   * If user has set a language in the browser and it is supported - it will return that language.
   * If no - then browser language if it is supported
   * If no - then default language
   * @param params
   */
  getUserLanguage(params: {
      supportedLang: TLangCode[],
      defaultLang: TLangCode,
      languageType?: TLanguageType
    }): TLangCode {
      const { supportedLang, defaultLang, languageType } = params;

      const preference = this.getLanguagePreference(languageType);

      if (preference && supportedLang.includes(preference)) {
        return preference;
      }

      if (this.browserLang && supportedLang.includes(this.browserLang as TLangCode)) {
        return this.browserLang as TLangCode;
      }

      return defaultLang;
    }

}
